import { CheckCircle2, CircleAlert, Info, X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

type Tone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
  /**
   * Leave the toast up after this one is pressed.
   *
   * Copying a number is not the end of the interaction — the next thing the
   * operator wants is the Call button that was sitting beside it. Closing on
   * copy made the shortcut row destroy itself on first use.
   */
  keepOpen?: boolean;
  icon?: typeof Info;
  'aria-label'?: string;
}

interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: Tone;
  /** One optional follow-on, e.g. "View" on the thing just created. */
  action?: ToastAction;
  /** Several, for a reminder you act on rather than merely read. */
  actions?: ToastAction[];
  /**
   * Stay until dismissed.
   *
   * A confirmation has been read by the time it fades. A reminder is a job:
   * it arrives while the operator is mid-sentence with somebody else, and
   * four and a half seconds later the number they needed is gone.
   */
  persist?: boolean;
}

export interface ToastApi {
  toast: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string, action?: ToastAction) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<Tone, { icon: typeof Info; ring: string; iconClass: string }> = {
  success: { icon: CheckCircle2, ring: 'border-ok/30', iconClass: 'text-ok' },
  error: { icon: CircleAlert, ring: 'border-danger/30', iconClass: 'text-danger' },
  info: { icon: Info, ring: 'border-info/30', iconClass: 'text-info' },
};

/** Long enough to read a sentence, short enough not to sit over the next click. */
const DISMISS_MS = 4500;
const ERROR_DISMISS_MS = 9000;

/**
 * Confirmation that something happened.
 *
 * Every create in this app either navigated away or silently closed its
 * dialog, which leaves the operator unable to tell a success from a failure
 * that was swallowed — the report was literally "no confirmation box, user
 * doesn't know it's created or not". A CRM where you cannot tell whether the
 * note saved gets the note typed twice.
 *
 * `aria-live="polite"` rather than `assertive`: a confirmation should be
 * announced at the next natural pause, not interrupt what is being read.
 * Errors linger twice as long, because they usually need acting on.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);

  const toast = useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      // Capped: a burst of writes should not bury the screen. The cap is also
      // what keeps `persist` safe — a quiet afternoon of reminders cannot
      // stack up more than three.
      setToasts((all) => [...all.slice(-2), { ...t, id }]);
      if (t.persist) return;
      window.setTimeout(() => dismiss(id), t.tone === 'error' ? ERROR_DISMISS_MS : DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, description, action) => toast({ title, description, tone: 'success', action }),
      error: (title, description) => toast({ title, description, tone: 'error' }),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Kept clear of what owns the bottom of the screen: the tab bar and
          the "New lead" button on a phone, the button alone on a laptop. */}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] sm:items-end sm:pb-24"
        >
          {toasts.map((t) => {
            const { icon: Icon, ring, iconClass } = TONE[t.tone];
            return (
              <div
                key={t.id}
                role="status"
                className={cn(
                  'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-[var(--radius-card)] border bg-surface p-3.5 shadow-lg',
                  'animate-in slide-in-from-bottom-2 fade-in-0',
                  ring,
                )}
              >
                <Icon className={cn('mt-0.5 size-5 shrink-0', iconClass)} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-ink">{t.title}</p>
                  {t.description && <p className="mt-0.5 text-sm text-muted">{t.description}</p>}
                  {/* `action` and `actions` are one row. The singular form
                      stays because a few dozen call sites pass it, and a
                      reminder needs several — merging them here means neither
                      caller has to know about the other. */}
                  {(() => {
                    const all = [...(t.action ? [t.action] : []), ...(t.actions ?? [])];
                    if (all.length === 0) return null;
                    return (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        {all.map((a) => {
                          const ActionIcon = a.icon;
                          return (
                            <button
                              key={a.label}
                              type="button"
                              aria-label={a['aria-label'] ?? a.label}
                              onClick={() => {
                                a.onClick();
                                if (!a.keepOpen) dismiss(t.id);
                              }}
                              className="inline-flex items-center gap-1 text-sm font-semibold text-brand underline-offset-4 hover:underline"
                            >
                              {ActionIcon && <ActionIcon className="size-3.5" aria-hidden="true" />}
                              {a.label}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  className="shrink-0 rounded p-0.5 text-muted hover:text-ink"
                >
                  <X className="size-4" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/**
 * Throws when used outside the provider rather than returning a no-op: a
 * confirmation that silently does nothing is the bug this exists to fix.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
